import { useState } from 'react'
import { SimpleGrid, Paper, Text, Group, Stack, Badge, UnstyledButton, Collapse } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import { EvidenceQuote } from './EvidenceQuote'

const skillMeta = {
  question_asking: { label: 'Question Asking', icon: '❓' },
  collaboration_language: { label: 'Collaboration', icon: '🤝' },
  empathy: { label: 'Empathy', icon: '💛' },
  critical_thinking: { label: 'Critical Thinking', icon: '🧠' },
  leadership: { label: 'Leadership', icon: '🌟' },
  conflict_resolution: { label: 'Conflict Resolution', icon: '⚖️' },
  creativity: { label: 'Creativity', icon: '💡' },
  adaptability: { label: 'Adaptability', icon: '🔄' },
}

function SkillCard({ indicator }) {
  const [expanded, setExpanded] = useState(false)
  // Support both legacy shape {skill, detected, count, examples} and AI shape {name, score, category}
  const skill = indicator.skill || indicator.name
  const detected = indicator.detected ?? (indicator.score != null ? indicator.score > 0 : false)
  const count = indicator.count ?? (indicator.score != null ? indicator.score : 0)
  const examples = indicator.examples || []
  const meta = skillMeta[skill] || { label: (skill || 'Unknown').replace(/_/g, ' '), icon: '📊' }

  return (
    <Paper
      p="sm"
      className="glass"
      style={{
        borderColor: detected
          ? 'rgba(0, 229, 255, 0.15)'
          : 'rgba(255, 255, 255, 0.05)',
        opacity: detected ? 1 : 0.5,
      }}
    >
      <Stack gap="xs">
        <Group gap="sm">
          <Text size="lg" style={{ lineHeight: 1 }}>
            {meta.icon}
          </Text>
          <Stack gap={2} style={{ flex: 1 }}>
            <Text size="xs" fw={700} ff="Orbitron, sans-serif" c="cyan.3" tt="capitalize">
              {meta.label}
            </Text>
            <Group gap={6}>
              <Badge
                size="xs"
                variant="light"
                color={detected ? 'cyan' : 'gray'}
              >
                {detected ? (indicator.score != null ? `Score: ${indicator.score}` : 'Detected') : 'Not Detected'}
              </Badge>
              {detected && indicator.category && (
                <Text size="xs" c="dimmed" tt="capitalize">
                  {indicator.category.replace(/_/g, ' ')}
                </Text>
              )}
              {detected && count > 0 && !indicator.score && (
                <Text size="xs" c="dimmed">
                  {count} instance{count !== 1 ? 's' : ''}
                </Text>
              )}
            </Group>
          </Stack>
        </Group>

        {detected && examples.length > 0 && (
          <>
            <UnstyledButton
              onClick={() => setExpanded((v) => !v)}
              style={{ width: '100%' }}
            >
              <Group gap={4} justify="center">
                <Text size="xs" c="dimmed" fw={600}>
                  {expanded ? 'Hide' : 'Examples'}
                </Text>
                <IconChevronDown
                  size={12}
                  color="var(--mantine-color-dimmed)"
                  style={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Group>
            </UnstyledButton>

            <Collapse in={expanded}>
              <Stack gap={0} mt={4}>
                {examples.map((ev, i) => (
                  <EvidenceQuote key={i} evidence={ev} />
                ))}
              </Stack>
            </Collapse>
          </>
        )}
      </Stack>
    </Paper>
  )
}

export function SkillIndicatorGrid({ indicators = [] }) {
  if (indicators.length === 0) return null

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {indicators.map((indicator, i) => (
        <SkillCard key={i} indicator={indicator} />
      ))}
    </SimpleGrid>
  )
}
